/**
 * Module: copilot/sseParser
 * Responsibility: Decodes a raw `ReadableStream<Uint8Array>` (the body of a
 *   fetch response) into a sequence of typed `CopilotSSEEvent` objects.
 * Collaborators: events.ts (the output type), CopilotClient.ts (the caller).
 * Concurrency: purely sequential — the generator processes one chunk at a time
 *   and yields events in stream order. It is not safe to call `parseSseStream`
 *   concurrently on the same `ReadableStream` because `getReader()` locks the stream.
 * CRLF handling: CRLF sequences (`\r\n`) are normalized to LF (`\n`) on the
 *   accumulated buffer — not per chunk — so a `\r\n` split across a network
 *   chunk boundary still normalizes correctly.
 */

import type { CopilotSSEEvent } from './events'

/**
 * Parse one complete SSE frame into events. A frame may contain multiple
 * `data:` lines; all of them are parsed (the spec allows several per frame).
 * Malformed JSON lines are silently swallowed so a single corrupt chunk
 * cannot tear down a live stream.
 */
function parseFrame(block: string): CopilotSSEEvent[] {
  const events: CopilotSSEEvent[] = []
  for (const line of block.split('\n')) {
    // Only `data:` lines carry application payload; `event:`, `id:`, retry and
    // comment lines (`:`) are valid SSE but ignored here.
    if (!line.startsWith('data: ')) continue
    try {
      // Cast to CopilotSSEEvent: the server is the authoritative source of
      // truth; unknown event types are passed through and the consumer can
      // ignore them. We do not validate the schema here to keep the parser
      // fast and dependency-free.
      events.push(JSON.parse(line.slice('data: '.length)) as CopilotSSEEvent)
    } catch {
      // Swallow JSON parse errors — a single malformed frame should not
      // abort the entire stream.
    }
  }
  return events
}

/**
 * Parses a Server-Sent Events stream into a sequence of `CopilotSSEEvent` values.
 *
 * The SSE wire format uses `\n\n` as the frame delimiter. Each frame may contain
 * multiple lines; this parser only cares about `data: <json>` lines and ignores
 * `event:`, `id:`, and comment lines (`:`) per the SSE spec.
 *
 * On stream end the remaining buffer is flushed and parsed, so a final frame
 * sent without a trailing blank line before EOF is still delivered.
 *
 * The `finally` block cancels the reader (tearing down the underlying HTTP
 * connection) and releases the lock — so breaking out of the generator early
 * (e.g. on `turn.error`) does not leave the SSE connection open until GC.
 *
 * @param stream - The raw byte stream from a fetch response body. Must not have
 *   been previously read (calling `getReader()` on an already-locked stream throws).
 * @returns An async generator that yields one `CopilotSSEEvent` per well-formed
 *   SSE data line. The generator completes when the underlying stream closes.
 * @throws Will re-throw errors from the underlying `ReadableStreamDefaultReader`
 *   (e.g. network abort, stream cancellation) — those are infrastructure errors
 *   the caller should surface.
 *
 * @example
 * ```typescript
 * const res = await fetch("/copilot/chat", { ... });
 * for await (const event of parseSseStream(res.body!)) {
 *   console.log(event);
 * }
 * ```
 */
export async function* parseSseStream(stream: ReadableStream<Uint8Array>): AsyncGenerator<CopilotSSEEvent> {
  // TextDecoder with `stream: true` handles multi-byte characters that span
  // chunk boundaries (e.g. emoji or CJK text in assistant responses).
  const decoder = new TextDecoder()
  const reader = stream.getReader()

  // `buffer` accumulates decoded text that arrived after the last `\n\n`
  // delimiter. It is grown by each incoming chunk and drained by the splitter.
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        break
      }

      // Append decoded bytes, then normalize CRLF → LF on the ACCUMULATED
      // buffer: normalizing per chunk would leave a lone `\r` behind when the
      // `\r\n` pair is split across two chunks, silently merging two frames.
      // `{ stream: true }` tells the decoder not to flush incomplete multi-byte
      // sequences — they will be completed by the next chunk.
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n')

      // Split on the SSE frame delimiter. The last element is an incomplete
      // frame (or empty string) and must be retained in the buffer.
      const parts = buffer.split('\n\n')

      // `pop()` removes and returns the last element — the trailing partial frame.
      // If `split` produces at least one element `pop()` always returns a string,
      // but we default to "" for the type-system's benefit.
      buffer = parts.pop() ?? ''

      for (const block of parts) {
        yield* parseFrame(block)
      }
    }

    // EOF: flush the decoder's pending multi-byte state and parse whatever is
    // left — a server that closes without a trailing blank line would otherwise
    // silently lose its final event (e.g. a trailing `usage.turn` or `turn.done`).
    buffer = (buffer + decoder.decode()).replace(/\r\n/g, '\n')
    if (buffer.length > 0) {
      for (const block of buffer.split('\n\n')) {
        yield* parseFrame(block)
      }
    }
  } finally {
    // Cancel the underlying stream — releasing the lock alone does NOT close
    // the HTTP connection when the caller exits the generator early. `cancel`
    // is a no-op on an already-finished stream, and any error here (e.g. the
    // fetch was aborted) is irrelevant to the caller.
    try {
      await reader.cancel()
    } catch {
      // stream already errored/aborted — nothing to clean up
    } finally {
      reader.releaseLock()
    }
  }
}
